// src/lib/observe.client.ts — captură de erori FRONTEND → backend (/api/client-log), care le
// trimite mai departe la OpenObserve (dacă e configurat). Fără SDK, fără token în browser.
// Throttle simplu (max 10/min) ca o buclă de erori să nu inunde. RUM complet (Web Vitals,
// session replay) rămâne un pas separat cu @openobserve/browser-rum + token client de ingestie.

let sent = 0
let window_start = 0

function report(message: string, stack: string, level = "error"): void {
	const now = Date.now()
	if (now - window_start > 60_000) {
		window_start = now
		sent = 0
	}
	if (sent >= 10) {
		return
	}
	sent++
	void fetch("/api/client-log", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ message, stack, level, url: location.href }),
		keepalive: true,
	}).catch(() => {
		/* raportarea nu aruncă niciodată */
	})
}

/** Trimite o METRICĂ de performanță (level "metric") cu câmpuri numerice — la OpenObserve prin backend. */
function metric(name: string, fields: Record<string, number>): void {
	void fetch("/api/client-log", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ level: "metric", message: name, url: location.href, ...fields }),
		keepalive: true,
	}).catch(() => {})
}

/**
 * Metrici de performanță FRONTEND (F13, „publică metrici ca să verificăm"): timpii de navigare
 * (DNS/TCP/TTFB/DOM/load) + Web Vitals disponibile fără SDK (LCP prin PerformanceObserver, INP/CLS
 * agregat). Zero dependințe; totul → /api/client-log → OpenObserve (când OO_LOGS e configurat).
 */
function install_perf_metrics(): void {
	// 1. Navigation Timing — după `load`.
	const on_load = () => {
		try {
			const nav = performance.getEntriesByType("navigation")[0] as
				| PerformanceNavigationTiming
				| undefined
			if (nav) {
				metric("page_perf", {
					ttfb_ms: Math.round(nav.responseStart),
					dom_ms: Math.round(nav.domContentLoadedEventEnd),
					load_ms: Math.round(nav.loadEventEnd),
					transfer_kb: Math.round(nav.transferSize / 1024),
				})
			}
		} catch {
			/* perf API absentă */
		}
	}
	if (document.readyState === "complete") {
		on_load()
	} else {
		window.addEventListener("load", () => setTimeout(on_load, 0), { once: true })
	}

	// 2. LCP (Largest Contentful Paint) — cea mai mare valoare până la primul input/ascundere.
	try {
		let lcp = 0
		const po = new PerformanceObserver((list) => {
			for (const e of list.getEntries()) {
				lcp = Math.max(
					lcp,
					(e as PerformanceEntry & { renderTime?: number; startTime: number }).startTime,
				)
			}
		})
		po.observe({ type: "largest-contentful-paint", buffered: true })
		const flush = () => {
			if (lcp > 0) {
				metric("web_vitals", { lcp_ms: Math.round(lcp) })
				lcp = 0
				po.disconnect()
			}
		}
		addEventListener("visibilitychange", () => document.hidden && flush(), { once: true })
		addEventListener("pagehide", flush, { once: true })
	} catch {
		/* LCP nesuportat (Safari vechi) */
	}
}

let installed = false
export function install_client_error_capture(): void {
	if (installed || typeof window === "undefined") {
		return
	}
	installed = true
	window.addEventListener("error", (e) => {
		report(String(e.message || "error"), e.error?.stack ?? "")
	})
	window.addEventListener("unhandledrejection", (e) => {
		const r = e.reason
		report(r instanceof Error ? r.message : String(r), r instanceof Error ? (r.stack ?? "") : "")
	})
	install_perf_metrics()
}

/** Marchează un moment de performanță din aplicație (ex. „harta a devenit interactivă"). */
export function mark_perf(name: string, ms: number): void {
	if (typeof window !== "undefined") {
		metric(name, { ms: Math.round(ms) })
	}
}
